import type { ComponentConfig } from '@puckeditor/core'
import ColorField from './ColorField'
import PaddingField, { type PaddingValue, paddingToStyle } from './PaddingField'

export interface ButtonBlockProps {
    text: string
    action: 'url' | 'close'
    url: string
    backgroundColor: string
    textColor: string
    fontSize: number
    fontWeight: string
    borderRadius: number
    paddingX: number
    paddingY: number
    fullWidth: boolean
    alignment: 'left' | 'center' | 'right'
    padding: PaddingValue
}

export const ButtonBlock: ComponentConfig<ButtonBlockProps> = {
    label: 'Button',
    defaultProps: {
        text: 'Got it',
        action: 'close',
        url: '',
        backgroundColor: '#000000',
        textColor: '#FFFFFF',
        fontSize: 16,
        fontWeight: '600',
        borderRadius: 30,
        paddingX: 24,
        paddingY: 15,
        fullWidth: true,
        alignment: 'center',
        padding: { top: 16, right: 0, bottom: 0, left: 0 },
    },
    resolveFields: (data, { fields }): any => {
        if (data.props.action === 'close') {
            const { url: _url, ...rest } = fields
            return rest
        }
        return fields
    },
    fields: {
        text: { type: 'text', label: 'Text', contentEditable: true },
        action: {
            type: 'radio',
            label: 'Action',
            options: [
                { label: 'Open URL', value: 'url' },
                { label: 'Close Popup', value: 'close' },
            ],
        },
        url: { type: 'text', label: 'URL' },
        backgroundColor: {
            type: 'custom',
            render: ({ value, onChange }) => <ColorField label="Background Color" value={value} onChange={onChange} />,
        },
        textColor: {
            type: 'custom',
            render: ({ value, onChange }) => <ColorField label="Text Color" value={value} onChange={onChange} />,
        },
        fontSize: { type: 'number', label: 'Font Size', min: 10, max: 48 },
        fontWeight: {
            type: 'select',
            label: 'Font Weight',
            options: [
                { label: 'Normal', value: '400' },
                { label: 'Medium', value: '500' },
                { label: 'Semi Bold', value: '600' },
                { label: 'Bold', value: '700' },
            ],
        },
        borderRadius: { type: 'number', label: 'Border Radius', min: 0, max: 100 },
        paddingX: { type: 'number', label: 'Horizontal Padding', min: 0, max: 100 },
        paddingY: { type: 'number', label: 'Vertical Padding', min: 0, max: 100 },
        fullWidth: { type: 'radio', label: 'Full Width', options: [{ label: 'Yes', value: true }, { label: 'No', value: false }] },
        alignment: {
            type: 'radio',
            label: 'Alignment',
            options: [
                { label: 'Left', value: 'left' },
                { label: 'Center', value: 'center' },
                { label: 'Right', value: 'right' },
            ],
        },
        padding: {
            type: 'custom',
            render: ({ value, onChange }) => <PaddingField value={value} onChange={onChange} />,
        },
    },
    render: ({ text, action, url, backgroundColor, textColor, fontSize, fontWeight, borderRadius, paddingX, paddingY, fullWidth, alignment, padding, puck }) => {
        const style: React.CSSProperties = {
            display: 'inline-block',
            backgroundColor,
            color: textColor,
            fontSize,
            fontWeight,
            borderRadius,
            padding: `${paddingY}px ${paddingX}px`,
            width: fullWidth ? '100%' : 'auto',
            textAlign: 'center',
            textDecoration: 'none',
            border: 'none',
            cursor: 'pointer',
            boxSizing: 'border-box',
            lineHeight: 1.4,
        }

        if (puck.isEditing) {
            return (
                <div style={{ textAlign: alignment, padding: paddingToStyle(padding) }}>
                    <span style={style}>{text}</span>
                </div>
            )
        }

        return (
            <div style={{ textAlign: alignment, padding: paddingToStyle(padding) }}>
                {action === 'url'
                    ? (
                        <a href={url} style={style} data-action="url" data-url={url}>
                            {text}
                        </a>
                    )
                    : (
                        <button style={style} data-action="close">
                            {text}
                        </button>
                    )}
            </div>
        )
    },
}
